import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, Gift, LogOut, Shield, ChevronDown, LogIn, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const NavbarSearchPanel = lazy(() => import('./NavbarSearchPanel'));

const dropdownBaseClasses =
  'absolute right-0 mt-3 min-w-[220px] border border-[#27272A] bg-[#121212] p-2 shadow-2xl transition-all duration-200 ease-out';
const dropdownVisibleClasses = 'opacity-100 translate-y-0';
const dropdownHiddenClasses = 'pointer-events-none opacity-0 -translate-y-2';

const desktopCategoryBaseClasses =
  'absolute top-full left-0 right-0 bg-[#121212] border-b border-white/10 py-8 transition-all duration-200 ease-out';

const mobileMenuBaseClasses =
  'md:hidden mt-4 border-t border-white/10 pt-4 overflow-hidden transition-all duration-200 ease-out';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const accountMenuRef = useRef(null);

  const categories = [
    { name: 'OVERSIZED TEES', slug: 'tees' },
    { name: 'HOODIES', slug: 'hoodies' },
    { name: 'CARGOS', slug: 'cargos' },
    { name: 'ACCESSORIES', slug: 'accessories' },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setShowAccountMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchResults = (query) => {
    if (!query) return;
    navigate(`/shop?search=${encodeURIComponent(query)}`);
    setShowSearch(false);
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
    setShowSearch(false);
  };

  const handleLogout = () => {
    setShowAccountMenu(false);
    setIsMenuOpen(false);
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-black/50 border-b border-white/10">
      <div className="max-w-screen-2xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 md:gap-2.5">
            <img
              src="/boldway-symbol-logo.png"
              alt="Boldway symbol"
              className="h-[2.7rem] w-[2.7rem] md:h-[3rem] md:w-[3rem] object-contain shrink-0"
            />
            <h1 className="font-anton text-[2rem] md:text-[2.2rem] leading-[0.9] tracking-[-0.01em] text-white">BOLDWAY</h1>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <button
              data-testid="shop-nav-link"
              onMouseEnter={() => setShowCategories(true)}
              onMouseLeave={() => setShowCategories(false)}
              onClick={() => navigate('/shop')}
              className="font-mono uppercase text-xs tracking-[0.2em] hover:text-[#FF5200] transition-colors"
            >
              SHOP
            </button>
            <Link
              to="/the-bold-way"
              data-testid="boldway-nav-link"
              className="font-mono uppercase text-xs tracking-[0.2em] hover:text-[#FF5200] transition-colors"
            >
              THE BOLD-WAY
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowSearch((prev) => !prev)}
              data-testid="search-button"
              className="hover:text-[#FF5200] transition-colors"
              aria-label="Search"
            >
              <Search className="w-5 h-5" strokeWidth={1.5} />
            </button>

            {user ? (
              <div className="flex items-center gap-4">
                <Link to="/loyalty" data-testid="loyalty-link" className="hover:text-[#FF5200] transition-colors" aria-label="Rewards">
                  <Gift className="w-5 h-5" strokeWidth={1.5} />
                </Link>
                <div className="relative" ref={accountMenuRef}>
                  <button
                    type="button"
                    onClick={() => setShowAccountMenu((prev) => !prev)}
                    data-testid="account-menu-button"
                    className="flex items-center gap-1 hover:text-[#FF5200] transition-colors"
                    aria-label="Account menu"
                  >
                    <User className="w-5 h-5" strokeWidth={1.5} />
                    <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <div
                    className={`${dropdownBaseClasses} ${showAccountMenu ? dropdownVisibleClasses : dropdownHiddenClasses}`}
                  >
                    <Link
                      to="/account"
                      onClick={() => setShowAccountMenu(false)}
                      className="flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-[#1E1E1E] hover:text-[#FF5200]"
                    >
                      <User className="w-4 h-4" strokeWidth={1.5} />
                      Account
                    </Link>
                    <Link
                      to="/loyalty"
                      onClick={() => setShowAccountMenu(false)}
                      className="flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-[#1E1E1E] hover:text-[#FF5200]"
                    >
                      <Gift className="w-4 h-4" strokeWidth={1.5} />
                      Rewards
                    </Link>
                    {user.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setShowAccountMenu(false)}
                        className="flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-[#1E1E1E] hover:text-[#FF5200]"
                      >
                        <Shield className="w-4 h-4" strokeWidth={1.5} />
                        Admin
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={handleLogout}
                      data-testid="logout-button"
                      className="flex w-full items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-[#1E1E1E] hover:text-[#FF5200]"
                    >
                      <LogOut className="w-4 h-4" strokeWidth={1.5} />
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Link to="/login" data-testid="login-link" className="hover:text-[#FF5200] transition-colors" aria-label="Login">
                <LogIn className="w-5 h-5" strokeWidth={1.5} />
              </Link>
            )}

            <Link to="/cart" data-testid="cart-link" className="relative">
              <ShoppingCart className="w-5 h-5 hover:text-[#FF5200] transition-colors" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#FF5200] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-mono">
                  {cartCount}
                </span>
              )}
            </Link>

            <button
              onClick={() => setIsMenuOpen((prev) => !prev)}
              className="md:hidden"
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        <Suspense fallback={<div className="mt-4 h-24 rounded-none border border-[#27272A] bg-[#121212]" />}>
          {showSearch && (
            <NavbarSearchPanel
              isOpen={showSearch}
              onClose={() => setShowSearch(false)}
              onProductClick={handleProductClick}
              onViewAllResults={handleSearchResults}
            />
          )}
        </Suspense>

        <div
          onMouseEnter={() => setShowCategories(true)}
          onMouseLeave={() => setShowCategories(false)}
          className={`${desktopCategoryBaseClasses} ${showCategories ? dropdownVisibleClasses : dropdownHiddenClasses}`}
        >
          <div className="max-w-screen-2xl mx-auto px-6 grid grid-cols-4 gap-6">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/shop?category=${cat.slug}`}
                data-testid={`category-${cat.slug}`}
                className="font-mono text-sm uppercase tracking-widest hover:text-[#FF5200] transition-colors"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div
          className={`${mobileMenuBaseClasses} ${isMenuOpen ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}
        >
          <div className="flex flex-col gap-4">
            <Link
              to="/shop"
              onClick={() => setIsMenuOpen(false)}
              data-testid="mobile-shop-link"
              className="font-mono text-xs uppercase tracking-widest"
            >
              SHOP
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/shop?category=${cat.slug}`}
                onClick={() => setIsMenuOpen(false)}
                data-testid={`mobile-category-${cat.slug}`}
                className="font-mono text-xs uppercase tracking-widest pl-4 text-gray-400"
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/the-bold-way"
              onClick={() => setIsMenuOpen(false)}
              data-testid="mobile-boldway-link"
              className="font-mono text-xs uppercase tracking-widest"
            >
              THE BOLD-WAY
            </Link>
            {user && (
              <>
                <Link
                  to="/loyalty"
                  onClick={() => setIsMenuOpen(false)}
                  className="font-mono text-xs uppercase tracking-widest"
                >
                  REWARDS
                </Link>
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    onClick={() => setIsMenuOpen(false)}
                    className="font-mono text-xs uppercase tracking-widest"
                  >
                    ADMIN
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="font-mono text-xs uppercase tracking-widest text-left"
                >
                  LOGOUT
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
